/* eslint-disable max-len */
import React from 'react';
import styled, { useTheme } from 'styled-components';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Area,
  Tooltip,
} from 'recharts';

import abbreviate from 'number-abbreviate';
import moment from 'moment';

function roundToNearest(num) {
  let factor = 1;

  if (num >= 5000) {
    factor = 1000;
  } else if (num >= 1000) {
    factor = 500;
  } else if (num >= 100) {
    factor = 100;
  } else {
    factor = 10;
  }

  return Math.ceil(num / factor) * factor;
}

const Container = styled.div`
  position: relative;
  margin: 40px 0 50px;

  .recharts-cartesian-axis-ticks {
    transform: translateY(5px);
    opacity: 0.8;
    font-weight: 400;
    color: ${({ theme }) => theme.less.textColor};

    line {
      opacity: 0.5;
    }

    text {
      transform: translateY(3px);
    }
  }
`;

const TooltipWrapper = styled.div`
  position: relative;
  padding: 15px 15px 10px;
  border-radius: 8px;
  color: ${({ theme }) => theme.less.textColor};
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.5;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TooltipData = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Details = styled.div`
  font-size: 0.7rem;
  font-weight: 400;
  color: ${({ theme }) => theme.less.textColor};
  opacity: 0.6;
`;

const Value = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.less.secondaryColor};
`;

const Date = styled(Details)`
  opacity: 0.3;
  margin-top: 10px;
`;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <TooltipWrapper>
        <TooltipData>
          <Group>
            <Details>Page Views</Details>
            <Value>{abbreviate(payload[0].payload.pageviews, 1)}</Value>
          </Group>
          <Group>
            <Details>Unique Visitors</Details>
            <Value>{abbreviate(payload[0].payload.visitors, 1)}</Value>
          </Group>
        </TooltipData>
        <Date>{moment(label).format('MMM D, YYYY')}</Date>
      </TooltipWrapper>
    );
  }

  return null;
};

function ElevationGraph({ data, aspectRatio, ...props }) {
  const { less: { primaryColor, secondaryColor } } = useTheme();

  return (
    <Container {...props}>
      <ResponsiveContainer width="100%" aspect={aspectRatio}>
        <ComposedChart data={data.items} margin={{ top: 10 }}>

          <defs>
            <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="20%" stopColor={primaryColor} />
              <stop offset="100%" stopColor={secondaryColor} />
            </linearGradient>
          </defs>

          <XAxis
            xAxisId={1}
            dataKey="value"
            domain={[data.items.at(1)?.value, data.items.at(-1)?.value]}
            tickFormatter={tick => (tick?.includes('T')
              ? moment(tick).format('h:mma')
              : moment(tick).format('MMM D'))}
          />

          <YAxis
            dataKey="pageviews"
            type="number"
            domain={[0, roundToNearest(data.pageviews.max)]}
            tickFormatter={tick => (tick === 0 ? '' : abbreviate(tick, 1))}
            hide="true"
          />

          <CartesianGrid stroke="hsla(0, 0%, 0%, 0.15)" strokeDasharray="5 5" />

          <Area
            xAxisId={1}
            dataKey="pageviews"
            stroke="transparent"
            fill="url(#gradient)"
            fillOpacity={1}
            type="monotone"
          />

          <Area
            xAxisId={1}
            dataKey="visitors"
            stroke="hsla(0,0%,100%,0.5)"
            strokeWidth={1.5}
            fill="transparent"
            type="monotone"
          />

          <Tooltip content={CustomTooltip} />
        </ComposedChart>
      </ResponsiveContainer>
    </Container>
  );
}

ElevationGraph.defaultProps = {
  data: [],
  aspectRatio: 2.5,
};

export default ElevationGraph;
